import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  doc,
  setDoc,
  addDoc,
  updateDoc,
  onSnapshot,
  collection,
  query,
  orderBy,
  serverTimestamp,
  writeBatch,
  increment,
} from 'firebase/firestore';
import { db } from '../firebase';
import { ensureVisitorUid } from '../utils/anonymousAuth';
import { notifyTelegram } from '../utils/notifyTelegram';

const ChatContext = createContext(null);

const SESSION_KEY = 'flare_support_session_id';
const CONTACT_KEY = 'flare_support_contact';
const TYPING_TIMEOUT_MS = 2000;
const AUTO_REPLY_DELAY_MS = 3000;

function getStoredSessionId() {
  return localStorage.getItem(SESSION_KEY);
}

function getStoredContact() {
  try {
    const raw = localStorage.getItem(CONTACT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function ChatProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(getStoredSessionId);
  const [contact, setContact] = useState(getStoredContact);
  const [messages, setMessages] = useState([]);
  const [adminTyping, setAdminTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  const view = !contact ? 'form' : 'chat';

  useEffect(() => {
    ensureVisitorUid().catch((err) => console.error('Anonymous sign-in failed:', err));
  }, []);

  useEffect(() => {
    if (!sessionId) return undefined;

    const messagesQuery = query(
      collection(db, 'chats', sessionId, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsubMessages = onSnapshot(messagesQuery, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    const unsubChat = onSnapshot(doc(db, 'chats', sessionId), (snap) => {
      setAdminTyping(Boolean(snap.data()?.adminTyping));
    });

    return () => {
      unsubMessages();
      unsubChat();
    };
  }, [sessionId]);

  useEffect(() => {
    if (!isOpen || !sessionId) return;
    const unseen = messages.filter((m) => m.sender === 'admin' && !m.seenByUser);
    if (unseen.length === 0) return;

    const batch = writeBatch(db);
    unseen.forEach((m) => {
      batch.update(doc(db, 'chats', sessionId, 'messages', m.id), { seenByUser: true });
    });
    batch.commit().catch((err) => console.error('Failed to mark messages seen:', err));
  }, [isOpen, sessionId, messages]);

  const startSession = useCallback(async ({ name, subject, email, firstMessage }) => {
    const visitorUid = await ensureVisitorUid();
    const newSessionId = crypto.randomUUID();

    await setDoc(doc(db, 'chats', newSessionId), {
      name,
      subject,
      email,
      visitorUid,
      createdAt: serverTimestamp(),
      lastMessageAt: serverTimestamp(),
      userTyping: false,
      adminTyping: false,
      unreadForAdmin: firstMessage ? 1 : 0,
    });

    if (firstMessage) {
      await addDoc(collection(db, 'chats', newSessionId, 'messages'), {
        sender: 'user',
        text: firstMessage,
        imageBase64: null,
        createdAt: serverTimestamp(),
        seenByAdmin: false,
        seenByUser: true,
      });

      notifyTelegram({ text: firstMessage, subject, email, fullName: name });
    }

    localStorage.setItem(SESSION_KEY, newSessionId);
    localStorage.setItem(CONTACT_KEY, JSON.stringify({ name, subject, email }));
    setSessionId(newSessionId);
    setContact({ name, subject, email });

    setTimeout(() => {
      addDoc(collection(db, 'chats', newSessionId, 'messages'), {
        sender: 'admin',
        text: `Hello ${name}, welcome to Flare Support! 👋\n\nOne of our support team members will assist you shortly.\n\nPlease stay in the chat and refresh the page occasionally to make sure you don't miss our response.\n\nThanks for your patience!`,
        createdAt: serverTimestamp(),
        seenByUser: false,
        seenByAdmin: true,
      }).catch((err) => console.error('Failed to send auto-reply:', err));
    }, AUTO_REPLY_DELAY_MS);
  }, []);

  const sendMessage = useCallback(async ({ text, imageBase64 }) => {
    if (!sessionId) return;
    await ensureVisitorUid();

    await addDoc(collection(db, 'chats', sessionId, 'messages'), {
      sender: 'user',
      text: text || '',
      imageBase64: imageBase64 || null,
      createdAt: serverTimestamp(),
      seenByAdmin: false,
      seenByUser: true,
    });

    await updateDoc(doc(db, 'chats', sessionId), {
      lastMessageAt: serverTimestamp(),
      userTyping: false,
      unreadForAdmin: increment(1),
    });

    if (text && text.trim()) {
      notifyTelegram({
        text,
        subject: contact?.subject,
        email: contact?.email,
        fullName: contact?.name,
      });
    } else if (imageBase64) {
      notifyTelegram({
        text: '📷 Sent an image',
        subject: contact?.subject,
        email: contact?.email,
        fullName: contact?.name,
      });
    }
  }, [sessionId, contact]);

  const setUserTyping = useCallback((typing) => {
    if (!sessionId) return;
    updateDoc(doc(db, 'chats', sessionId), { userTyping: typing }).catch(() => {});

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    if (typing) {
      typingTimeoutRef.current = setTimeout(() => {
        updateDoc(doc(db, 'chats', sessionId), { userTyping: false }).catch(() => {});
      }, TYPING_TIMEOUT_MS);
    }
  }, [sessionId]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ isOpen, open, close, view, messages, adminTyping, startSession, sendMessage, setUserTyping, contact }),
    [isOpen, open, close, view, messages, adminTyping, startSession, sendMessage, setUserTyping, contact]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside <ChatProvider>');
  return ctx;
}